import React, { useEffect, useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  Box, Grid, Typography, CircularProgress, TextField, Select,
  InputLabel, FormControl, MenuItem, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, TablePagination, Chip, TableSortLabel, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, Button
} from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import { formatDate } from '../../../helpers/timeHelper';
import { useNavigate } from 'react-router-dom';
import { makeApiRequest } from '../../../utils/api';
import { Visibility } from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';
import { Search } from '@mui/icons-material';

export const ListBranchAdmins = () => {
  const [selectedOption, setSelectedOption] = useState('All Locations');
  const [branchAdminData, setBranchAdminData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [openStatusModal, setOpenStatusModal] = useState(false);
  const navigate = useNavigate();

  const control = localStorage.getItem('control');
  const userLocation = localStorage.getItem('userLocation');
  const statusOptions = ['Active', 'Completed', 'Resigned', 'Abscond'];

  useEffect(() => {
    if (control != 1) {
      toast.error('You are not authorized to view this page');
      navigate('/dashboard');
    }
  }, []);

  const getStatusConfig = (status) => {
    const statusMap = {
      active: { color: '#298880', bgColor: '#CBFAF0', border: "1px solid #98EEDB", variant: 'filled' }, // Green
      completed: { color: '#03045E', bgColor: '#90E0EF', border: "1px solid #0077B6", variant: 'filled' }, // Blue
      resigned: { color: '#BA3068', bgColor: '#FDE6EB', border: "1px solid #FCD6DE", variant: 'filled' }, // Red
      abscond: { color: '#FF8000', bgColor: '#FFFFA3', border: "1px solid #FF9900", variant: 'filled' }, // Orange
    };
    return statusMap[status.toLowerCase()] || { color: '#757575', bgColor: '#F5F5F5' }; // Default gray
  };

  const fetchBranchAdminData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await makeApiRequest('post', '/api/team/list', {
        token: localStorage.getItem('authToken'),
        data: {
          location: (control === '2' || control === '4') && userLocation ? userLocation : selectedOption,
          control: 4,
          per_page: rowsPerPage,
          page: page + 1,
          search: searchQuery,
        }
      });

      if (response?.status === 'success') {
        setBranchAdminData(response.data.list || []);
        setTotalRecords(response.data.pagination?.total || 0);
      } else {
        throw new Error(response.message || 'Failed to fetch branch admin data');
      }
    } catch (err) {
      setError(err.message || 'Error fetching data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBranchAdminData();
  }, [selectedOption, page, rowsPerPage, order, orderBy, searchQuery]);

  const handleChange = (event) => {
    setSelectedOption(event.target.value);
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const sortedData = [...branchAdminData].sort((a, b) => {
    const aValue = a[orderBy] || "";
    const bValue = b[orderBy] || "";

    if (typeof aValue === "string" && typeof bValue === "string") {
      return order === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }
    if (typeof aValue === "number" && typeof bValue === "number") {
      return order === "asc" ? aValue - bValue : bValue - aValue;
    }
    return 0;
  });

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleView = (row) => {
    navigate(`/view-profile`, { state: { userid: row.userid } });
  };

  const handleOpenStatusModal = (row) => {
    setSelectedAdmin(row);
    setSelectedStatus(row.status);
    setOpenStatusModal(true);
  };

  const handleCloseStatusModal = () => {
    setOpenStatusModal(false);
    setSelectedAdmin(null);
    setSelectedStatus('');
  };

  const handleConfirmStatus = async () => {
    if (!selectedAdmin || !selectedStatus) {
      toast.error('Please select a status');
      return;
    }

    const currentStatus = selectedAdmin.status.toLowerCase();
    if (currentStatus === selectedStatus) {
      toast.info('No status change');
      handleCloseStatusModal();
      return;
    }

    try {
      const response = await makeApiRequest('post', '/api/employee/updateStatus', {
        token: localStorage.getItem('authToken'),
        data: {
          id: selectedAdmin.userid,
          estatus: selectedStatus,
        },
      });

      if (response?.status === 'success') {
        setBranchAdminData((prev) =>
          prev.map((admin) =>
            admin.userid === selectedAdmin.userid ? { ...admin, status: selectedStatus } : admin
          )
        );
        toast.success(`${selectedAdmin.name} moved from ${currentStatus} to ${selectedStatus}`);
        handleCloseStatusModal();
      } else {
        toast.error(response?.message || 'Failed to update status');
      }
    } catch (err) {
      console.error('Error updating status:', err);
      toast.error(err.message || 'Error updating status');
    }
  };

  return (
    <Box sx={{ backgroundColor: "#ECEDFF", minHeight: '100vh' }}>
      <ToastContainer
        position="top-center"
        autoClose={2000}
        hideProgressBar={false}
        newestOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover={false}
        theme="light"
      />
      <Grid container sx={{ height: "auto", display: "flex", justifyContent: "center", alignItems: "" }}>
        <Grid item xs={11} sx={{ height: "80px", display: "flex", alignItems: "center" }}>
          <Typography variant='h4' sx={{ color: "#481E82", fontSize: { xs: '24px', sm: '34px' } }}>Branch Admin's List</Typography>
        </Grid>

        <Grid item xs={11} sx={{ backgroundColor: "white", borderRadius: "5px 5px 0 0" }}>
          <Grid container sx={{ borderRadius: "5px 5px 0 0", display: "flex", flexDirection: { xs: 'column', sm: 'row' }, justifyContent: "space-between", alignItems: "center", boxShadow: 3, padding: { xs: '20px 0px', sm: '0px' }, gap: { xs: '20px', sm: 0 } }}>

            <Grid item xs={12} sm={6} md={4} lg={3} sx={{ height: "100px", width: "100%", borderRadius: "5px", backgroundColor: 'white', padding: "0px", display: "flex", justifyContent: "", alignItems: "center" }}>
              <Box sx={{ width: "100%", display: "flex", justifyContent: "", alignItems: "center" }}>
                <TextField
                  id="search-box"
                  variant="outlined"
                  size="small"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  sx={{
                    mx: 2,
                    width: "100%"
                  }}
                  placeholder='Search'
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={3} sx={{ height: "100px", width: '100%', display: "flex", alignItems: "center", justifyContent: { xs: "center", sm: "end" } }}>
              {control == 1 &&
                <FormControl fullWidth sx={{ mx: 2, maxWidth: { xs: '100%', sm: 300 } }} size="small">
                  <InputLabel id="dropdown-label">Select Location</InputLabel>
                  <Select
                    labelId="dropdown-label"
                    id="dropdown"
                    value={selectedOption}
                    label="Select Location"
                    onChange={handleChange}
                  >
                    <MenuItem value="All Locations">All Locations</MenuItem>
                    <MenuItem value="Chennai">Chennai</MenuItem>
                    <MenuItem value="Trichy">Trichy</MenuItem>
                  </Select>
                </FormControl>
              }
            </Grid>

          </Grid>
          <Grid item xs={12} sx={{ backgroundColor: "white", marginTop: "", padding: "" }}>
            <TableContainer component={Paper} sx={{ borderRadius: 0 }}>
              <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#ECEDFF" }}>
                    <TableCell>
                      <TableSortLabel active={orderBy === "name"} direction={orderBy === "name" ? order : "asc"} onClick={() => handleSort("name")}>
                        <strong>Name</strong>
                      </TableSortLabel>
                    </TableCell>

                    <TableCell>
                      <TableSortLabel active={orderBy === "email"} direction={orderBy === "email" ? order : "asc"} onClick={() => handleSort("email")}>
                        <strong>Email</strong>
                      </TableSortLabel>
                    </TableCell>

                    <TableCell>
                      <TableSortLabel active={orderBy === "dob"} direction={orderBy === "dob" ? order : "asc"} onClick={() => handleSort("dob")}>
                        <strong>DOB</strong>
                      </TableSortLabel>
                    </TableCell>

                    <TableCell>
                      <TableSortLabel active={orderBy === "mobileNum"} direction={orderBy === "mobileNum" ? order : "asc"} onClick={() => handleSort("mobileNum")}>
                        <strong>Mobile Number</strong>
                      </TableSortLabel>
                    </TableCell>

                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Action</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedData.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell>{row.name}<br /><Typography variant="body2" sx={{ fontSize: '13px', color: 'gray' }}>{row.location} | {row.userid}</Typography></TableCell>
                      <TableCell>{row.email}</TableCell>
                      <TableCell>{formatDate(row.dob)}</TableCell>
                      <TableCell>{row.mobileNum}</TableCell>
                      <TableCell>
                        <Chip
                          label={row.status.charAt(0).toUpperCase() + row.status.slice(1)}
                          size="small"
                          onClick={() => handleOpenStatusModal(row)}
                          sx={{
                            minWidth: '80px',
                            backgroundColor: getStatusConfig(row.status).bgColor,
                            color: getStatusConfig(row.status).color,
                            padding: '4px 10px',
                            borderRadius: '4px',
                            height: '28px',
                            border: getStatusConfig(row.status).border,
                            width: '110px',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            '&:hover': {
                              backgroundColor: getStatusConfig(row.status).bgColor,
                              opacity: 0.9,
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View">
                          <IconButton onClick={() => handleView(row)} aria-label="view" sx={{ color: "#1976D2" }}>
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={totalRecords}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Grid>

        </Grid>
      </Grid>
      {loading && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress
            size={40}
            thickness={4}
            sx={{
              color: '#481E82',
            }}
          />
        </Box>
      )}
      <Dialog
        open={openStatusModal}
        onClose={handleCloseStatusModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 'bold' }}>Change Status</DialogTitle>
        <DialogContent sx={{ padding: '24px' }}>
          <FormControl fullWidth size="medium" sx={{ mt: 3 }}>
            <InputLabel id="status-select-label">Status</InputLabel>
            <Select
              labelId="status-select-label"
              value={selectedStatus}
              label="Status"
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              {statusOptions.map((status) => (
                <MenuItem key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button
            onClick={handleCloseStatusModal}
            variant="outlined"
            size="large"
            sx={{ minWidth: '120px', fontSize: '1rem' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmStatus}
            variant="contained"
            color="primary"
            size="large"
            sx={{ minWidth: '120px', fontSize: '1rem' }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
